import React from 'react';
import {ColorValue, StyleSheet, View, ViewProps, ViewStyle} from 'react-native';
import {DimensionValue} from "react-native/Libraries/StyleSheet/StyleSheetTypes";

interface DivProps extends ViewProps {
  borderColor?: string;
  borderTopColor?: string;
  borderRightColor?: string;
  borderBottomColor?: string;
  borderLeftColor?: string;
  borderWidth?: number;
  borderHorizontalWidth?: number;
  borderTopWidth?: number;
  borderRightWidth?: number;
  borderBottomWidth?: number;
  borderLeftWidth?: number;
  borderStartWidth?: number;
  borderEndWidth?: number;
  borderStyle?: ViewStyle['borderStyle'];
  m?: number | string;
  mt?: number | string;
  mr?: number | string;
  mb?: number | string;
  ml?: number | string;
  mx?: number | string;
  my?: number | string;
  ms?: number;
  p?: number | string;
  pt?: number | string;
  pr?: number | string;
  pb?: number | string;
  pl?: number | string;
  px?: number | string;
  py?: number | string;
  ps?: number;
  rounded?: number;
  roundedTopLeft?: number;
  roundedTopRight?: number;
  roundedBottomLeft?: number;
  roundedBottomRight?: number;
  roundedTop?: number;
  roundedLeft?: number;
  roundedRight?: number;
  roundedBottom?: number;
  shadowColor?: string;
  minH?: number | string;
  minW?: number | string;
  maxH?: number | string;
  maxW?: number | string;
  h?: DimensionValue;
  w?: number | string;
  row?: boolean;
  flex?: ViewStyle['flex'];
  flexDir?: ViewStyle['flexDirection'];
  flexWrap?: ViewStyle['flexWrap'];
  justifyContent?: ViewStyle['justifyContent'];
  alignSelf?: ViewStyle['alignSelf'];
  alignItems?: ViewStyle['alignItems'];
  position?: ViewStyle['position'];
  top?: DimensionValue;
  right?: DimensionValue;
  bottom?: DimensionValue;
  left?: DimensionValue;
  opacity?: number;
  overflow?: ViewStyle['overflow'];
  zIndex?: number;
  bg?: ColorValue;
  column?: boolean;
  flexShrink?: number;
  gap?: number;
}

const Div = (props: DivProps) => {
  const {
    children, borderColor, borderTopColor, borderRightColor, borderBottomColor, borderLeftColor, borderWidth,
    borderTopWidth, borderRightWidth, borderBottomWidth, borderLeftWidth, borderStartWidth, borderEndWidth, borderStyle,
    m, mt, mr, mb, ml, mx, my, ms, p, pt, pr, pb, pl, px, py, ps, rounded, roundedTopLeft, roundedTopRight,
    roundedBottomLeft, roundedBottomRight, roundedTop, roundedLeft, roundedRight, roundedBottom, shadowColor, minH,
    minW, maxH, maxW, h, w, row, flex, flexDir, flexWrap, justifyContent, alignSelf, alignItems, position, top, right,
    bottom, left, opacity, overflow, zIndex, bg, column, borderHorizontalWidth, flexShrink, gap, ...rest
  } = props;

  // RN's ViewStyle.{margin|padding|*Width|...} narrowed to DimensionValue
  // (number | "auto" | `${number}%`) in @types/react-native ≥ 0.71. The
  // DivProps API has historically accepted `number | string` for the
  // shorthand props (m/mt/p/...), which is wider than DimensionValue.
  // The assembled object is typed as Record<string, unknown> so the
  // shorthand-to-style merge survives — RN validates styles at
  // runtime; the final cast at the View props boundary narrows.
  let style: Record<string, unknown> = {
    ...(borderColor ? {borderColor} : {}),
    ...(borderTopColor ? {borderTopColor} : {}),
    ...(borderRightColor ? {borderRightColor} : {}),
    ...(borderBottomColor ? {borderBottomColor} : {}),
    ...(borderLeftColor ? {borderLeftColor} : {}),
    ...(borderWidth !== undefined ? {borderWidth} : {}),
    ...(borderHorizontalWidth !== undefined ? {borderLeftWidth: borderHorizontalWidth, borderRightWidth: borderHorizontalWidth} : {}),
    ...(borderTopWidth !== undefined ? {borderTopWidth} : {}),
    ...(borderRightWidth !== undefined ? {borderRightWidth} : {}),
    ...(borderBottomWidth !== undefined ? {borderBottomWidth} : {}),
    ...(borderLeftWidth !== undefined ? {borderLeftWidth} : {}),
    ...(borderStartWidth !== undefined ? {borderStartWidth} : {}),
    ...(borderEndWidth !== undefined ? {borderEndWidth} : {}),
    ...(borderStyle ? {borderStyle} : {}),
    ...(m !== undefined ? {margin: m} : {}),
    ...(mt !== undefined ? {marginTop: mt} : {}),
    ...(mr !== undefined ? {marginRight: mr} : {}),
    ...(mb !== undefined ? {marginBottom: mb} : {}),
    ...(ml !== undefined ? {marginLeft: ml} : {}),
    ...(mx !== undefined ? {marginHorizontal: mx} : {}),
    ...(my !== undefined ? {marginVertical: my} : {}),
    ...(ms !== undefined ? {marginStart: ms} : {}),
    ...(p !== undefined ? {padding: p} : {}),
    ...(pt !== undefined ? {paddingTop: pt} : {}),
    ...(pr !== undefined ? {paddingRight: pr} : {}),
    ...(pb !== undefined ? {paddingBottom: pb} : {}),
    ...(pl !== undefined ? {paddingLeft: pl} : {}),
    ...(px !== undefined ? {paddingHorizontal: px} : {}),
    ...(py !== undefined ? {paddingVertical: py} : {}),
    ...(ps !== undefined ? {paddingStart: ps} : {}),
    ...(rounded !== undefined ? {borderRadius: rounded} : {}),
    ...(roundedTopLeft !== undefined ? {borderTopLeftRadius: roundedTopLeft} : {}),
    ...(roundedTopRight !== undefined ? {borderTopRightRadius: roundedTopRight} : {}),
    ...(roundedBottomLeft !== undefined ? {borderBottomLeftRadius: roundedBottomLeft} : {}),
    ...(roundedBottomRight !== undefined ? {borderBottomRightRadius: roundedBottomRight} : {}),
    ...(roundedTop !== undefined ? {borderTopLeftRadius: roundedTop, borderTopRightRadius: roundedTop} : {}),
    ...(roundedLeft !== undefined ? {borderTopLeftRadius: roundedLeft, borderBottomLeftRadius: roundedLeft} : {}),
    ...(roundedRight !== undefined ? {borderTopRightRadius: roundedRight, borderBottomRightRadius: roundedRight} : {}),
    ...(roundedBottom !== undefined ? {borderBottomLeftRadius: roundedBottom, borderBottomRightRadius: roundedBottom} : {}),
    ...(shadowColor ? {shadowColor} : {}),
    ...(minH !== undefined ? {minHeight: minH} : {}),
    ...(minW !== undefined ? {minWidth: minW} : {}),
    ...(maxH !== undefined ? {maxHeight: maxH} : {}),
    ...(maxW !== undefined ? {maxWidth: maxW} : {}),
    ...(h !== undefined ? {height: h} : {}),
    ...(w !== undefined ? {width: w} : {}),
    ...(row ? {flexDirection: 'row'} : {}),
    ...(flex !== undefined ? {flex} : {}),
    ...(flexDir ? {flexDirection: flexDir} : {}),
    ...(flexWrap ? {flexWrap} : {}),
    ...(justifyContent ? {justifyContent} : {}),
    ...(alignSelf ? {alignSelf} : {}),
    ...(alignItems ? {alignItems} : {}),
    ...(position ? {position} : {}),
    ...(top !== undefined ? {top} : {}),
    ...(right !== undefined ? {right} : {}),
    ...(bottom !== undefined ? {bottom} : {}),
    ...(left !== undefined ? {left} : {}),
    ...(opacity !== undefined ? {opacity} : {}),
    ...(overflow ? {overflow} : {}),
    ...(zIndex !== undefined ? {zIndex} : {}),
    ...(bg ? {backgroundColor: bg} : {}),
    ...(column ? {flexDirection: 'column'} : {}),
    ...(flexShrink !== undefined ? {flexShrink} : {}),
    ...(gap !== undefined ? {gap} : {})
  };

  const combinedStyle = props && props.style ? StyleSheet.flatten(props.style) : {};
  style = {...style, ...combinedStyle};
  if (rest && rest.style) delete rest.style;

  return <View style={StyleSheet.flatten(style as Partial<ViewStyle>)} {...rest}>{children}</View>;
};

export default React.memo(Div);
